import React, { useState, useEffect, useCallback } from 'react';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import '../../styles/shared/Carousel.css';

interface Slide {
  id: number;
  image: string;
  tag?: string;
  title: string;
  subtitle?: string;
  cta?: { label: string; onClick?: () => void };
  bgColor?: string;
}

interface CarouselProps {
  slides: Slide[];
  autoPlay?: boolean;
  interval?: number;
}

const Carousel: React.FC<CarouselProps> = ({ slides, autoPlay = true, interval = 5000 }) => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(next, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, next]);

  if (slides.length === 0) return null;

  const slide = slides[current];

  return (
    <div className="carousel" style={{ background: slide.bgColor || '#0B1120' }}>
      <div className="carousel__inner">
        {/* Content */}
        <div className="carousel__content">
          {slide.tag && <span className="carousel__tag">{slide.tag}</span>}
          <h2 className="carousel__title">{slide.title}</h2>
          {slide.subtitle && <p className="carousel__subtitle">{slide.subtitle}</p>}
          {slide.cta && (
            <button className="carousel__cta" onClick={slide.cta.onClick}>
              {slide.cta.label} <MdArrowForward />
            </button>
          )}
        </div>

        {/* Image */}
        <div className="carousel__image-wrap">
          <img src={slide.image} alt={slide.title} className="carousel__image" />
        </div>
      </div>

      {/* Controls */}
      <button className="carousel__arrow carousel__arrow--left" onClick={prev} aria-label="Previous">
        <MdArrowBack />
      </button>
      <button className="carousel__arrow carousel__arrow--right" onClick={next} aria-label="Next">
        <MdArrowForward />
      </button>

      {/* Dots */}
      <div className="carousel__dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`carousel__dot ${i === current ? 'carousel__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
